import 'chart.js/auto';

import { useMemo, useState } from 'react';
import type { ChartData, ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Line } from 'react-chartjs-2';
import { Box, Stack, Theme, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useAsyncCallback } from 'react-async-hook';
import { useIntl } from 'react-intl';
import { makeStyles } from '@mui/styles';

import { NetReturnStatisticsResultDto } from 'api/investor-client';
import { investorApiClient } from 'api';
import Card from 'components/card';
import { Select } from 'components/forms';
import { SubmitFormOnChange } from 'components';
import { formatMoney, moveDecimalPoint } from 'utils';
import { barOptions, lineOptions } from './ExpectedRepayments/chart-options';
import { SyncCurrency } from 'pages/DashboardPage/ExpectedRepayments';
dayjs.extend(utc);

interface Props { currency: string }
interface Values {
  range: { from: Dayjs; to: Dayjs };
  currency: string;
}
type ChartView = 'stacked_bar' | 'line';
const useStyles = makeStyles((theme: Theme) => ({ card: { minHeight: '510px' } }));

export const NetReturnHistory = ({ currency }: Props) => {
  const intl = useIntl();
  const [dataset, setDataset] = useState<NetReturnStatisticsResultDto[]>([]);
  const [total, setTotal] = useState(0);
  const [chartView, setChartView] = useState<ChartView>('line');
  const classes = useStyles();

  const rangeOptions = useMemo(() => [
    { value: { from: dayjs.utc().subtract(5, 'months').startOf('month'), to: dayjs.utc().endOf('month') }, label: intl.formatMessage({ id: 'last_months' }, { nrOfMonths: 6 }) },
    { value: { from: dayjs.utc().subtract(11, 'months').startOf('month'), to: dayjs.utc().endOf('month') }, label: intl.formatMessage({ id: 'last_months' }, { nrOfMonths: 12 }) },
  ], [intl]);

  const data = useMemo((): ChartData<'bar'> => ({
    labels: dataset.map((ds) => [intl.formatMessage({ id: `month.${dayjs.utc(ds.date).format('MMMM').toLowerCase()}` }), dayjs.utc(ds.date).format('YYYY')]),
    datasets: [{ label: 'net_return', data: dataset.map((d) => moveDecimalPoint(d.netReturn, -2)), backgroundColor: '#254E8F' }],
  }), [dataset, intl]);

  const getStatistics = useAsyncCallback(async (values: Values) => {
    const { data } = await investorApiClient.investors.investorApiControllerGetNetReturnStatistics({
      from: values.range.from.toISOString(),
      to: values.range.to.toISOString(),
      currency: values.currency,
    });

    setDataset(data);
    setTotal(data.reduce((sum, item) => sum + item.netReturn, 0));
  });

  const lineData = useMemo((): ChartData<'line'> => ({
    labels: data.labels,
    datasets: [{
      label: intl.formatMessage({id:'net_return' }),
      data: data.datasets[0].data,
      borderColor: '#8B2BF4',
      backgroundColor: '#254E8F',
      pointBackgroundColor: '#8B2BF4',
      pointRadius: 4,
      pointHoverRadius: 5,
      tension: 0,
    }],
  }), [data, intl]);

  return (
    <Card className={classes.card}>
      <Card.Header
        variant="condensed"
        title={intl.formatMessage({ id: 'net_return_history' })}
      />

      <Card.Body loadingContent={getStatistics.loading}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Formik<Values>
            initialValues={{
              range: rangeOptions[1].value,
              currency,
            }}
            onSubmit={(v) => getStatistics.execute(v)}
          >
            <Form>
              <SyncCurrency currency={currency} />
              <Stack spacing={2}>
                <Field
                  id="range"
                  name="range"
                  sx={{ width: '150px' }}
                  component={Select}
                  options={rangeOptions}
                  variant="filter"
                  disableSelectedClass
                />

                <ToggleButtonGroup
                  color="primary"
                  size="small"
                  exclusive
                  value={chartView}
                  onChange={(_, value: ChartView | null) => {
                    if (value) setChartView(value);
                  }}
                >
                  <ToggleButton value="stacked_bar">
                    {intl.formatMessage({ id: 'chart_view_stacked_bar' })}
                  </ToggleButton>

                  <ToggleButton value="line">
                    {intl.formatMessage({ id: 'chart_view_line' })}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <SubmitFormOnChange initialSubmit />
            </Form>
          </Formik>

          <Box>
            <Typography variant="h6" component="span">
              {formatMoney(moveDecimalPoint(total, -2))}
            </Typography>
          </Box>
        </Box>

        {chartView === 'stacked_bar' ? (
          <Bar options={barOptions} data={data} plugins={[ChartDataLabels]} />
        ) : (
          <Line
            options={lineOptions}
            data={lineData}
            plugins={[ChartDataLabels]}
          />
        )}
      </Card.Body>
    </Card>
  );
  }
